// Text direction for the active language.
//
// The journey renders right-to-left for Arabic, Persian, Hebrew and Urdu
// (the four RTL languages in src/locales) plus the other RTL scripts a
// publisher could send. Everything else is left-to-right. `dir` is set on
// <html> from here (App.js) so the browser mirrors the layout, and the
// same value feeds Mantine's theme.dir so its components (Drawer slide
// direction, Text alignment, Popover placement) follow.

// ISO 639 codes of languages written right-to-left. Base language only:
// a region or script subtag never changes the direction, except the
// Latin-script variants handled below.
const RTL_LANGUAGES = new Set([
  "ar", // Arabic
  "fa", // Persian
  "he", // Hebrew
  "iw", // Hebrew (legacy code)
  "ur", // Urdu
  "ps", // Pashto
  "sd", // Sindhi
  "ug", // Uyghur
  "yi", // Yiddish
  "dv", // Dhivehi
  "ckb", // Central Kurdish (Sorani)
  "ku", // Kurdish (Arabic script by default outside Turkey)
  "arc", // Aramaic
  "syr", // Syriac
  "nqo", // N'Ko
]);

// Script subtags that are themselves right-to-left (zh-Arab is not a
// thing, but `pa-Arab`, `ks-Arab`, `az-Arab` are).
const RTL_SCRIPTS = new Set(["arab", "hebr", "syrc", "thaa", "nkoo", "adlm", "rohg", "mand"]);
// And the ones that force left-to-right for an RTL language (ku-Latn,
// ar-Latn transliterations).
const LTR_SCRIPTS = new Set(["latn", "cyrl"]);

/**
 * Normalize a language tag for inspection: string, trimmed, lowercase,
 * `_` → `-`. Returns "" when the input is not a usable string.
 */
export function normalizeTag(tag) {
  if (typeof tag !== "string") return "";
  return tag.trim().toLowerCase().replace(/_/g, "-");
}

/**
 * Whether `tag` names a right-to-left language ("fa", "ar-EG", "he_IL",
 * "ur-PK", "pa-Arab"). Unknown, empty or invalid input is left-to-right.
 */
export function isRtlLanguage(tag) {
  const normalized = normalizeTag(tag);
  if (!normalized) return false;
  const parts = normalized.split("-").filter(Boolean);
  if (parts.length === 0) return false;
  const script = parts.find((p, i) => i > 0 && p.length === 4);
  if (script && LTR_SCRIPTS.has(script)) return false;
  if (script && RTL_SCRIPTS.has(script)) return true;
  return RTL_LANGUAGES.has(parts[0]);
}

/** "rtl" or "ltr" for the `dir` attribute. */
export function getTextDirection(tag) {
  return isRtlLanguage(tag) ? "rtl" : "ltr";
}

/**
 * The value for the <html lang> attribute: the tag as sent, with `_`
 * replaced by `-` and BCP 47 casing (language lowercase, 4-letter script
 * title-case, 2–3 letter region uppercase). Falls back to "en".
 */
export function getHtmlLang(tag) {
  const normalized = normalizeTag(tag);
  if (!normalized || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(normalized)) return "en";
  return normalized
    .split("-")
    .map((part, i) => {
      if (i === 0) return part;
      if (part.length === 4) return part[0].toUpperCase() + part.slice(1);
      if (part.length === 2 || part.length === 3) return part.toUpperCase();
      return part;
    })
    .join("-");
}

/**
 * Write `lang` and `dir` onto <html>. Safe to call on every language
 * change; a no-op outside the browser.
 */
export function applyDocumentLanguage(tag) {
  if (typeof document === "undefined" || !document.documentElement) return;
  const root = document.documentElement;
  const lang = getHtmlLang(tag);
  const dir = getTextDirection(tag);
  if (root.getAttribute("lang") !== lang) root.setAttribute("lang", lang);
  if (root.getAttribute("dir") !== dir) root.setAttribute("dir", dir);
}
